"use client";

import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { trackEvent } from "@/lib/analytics/dataLayer";

function Popup() {
	const router = useRouter();
	const params = useSearchParams();
	const shouldShow = params.get("audit") === "confirm-sent";
	const [open, setOpen] = React.useState(shouldShow);
	// Guards the analytics push so it fires at most once while this popup is shown.
	const trackedRef = React.useRef(false);

	React.useEffect(() => {
		setOpen(shouldShow);
	}, [shouldShow]);

	// Fire `audit_confirm_sent` once, the moment the popup actually becomes shown.
	React.useEffect(() => {
		if (open && !trackedRef.current) {
			trackedRef.current = true;
			trackEvent("audit_confirm_sent");
		}
	}, [open]);

	const close = React.useCallback(() => {
		setOpen(false);
		// Drop the query param so a refresh doesn't re-open the popup.
		router.replace("/", { scroll: false });
	}, [router]);

	// Auto-dismiss after a few seconds.
	React.useEffect(() => {
		if (!open) return;
		const id = setTimeout(close, 8000);
		return () => clearTimeout(id);
	}, [open, close]);

	// Close on Escape.
	React.useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [open, close]);

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<div
				aria-hidden="true"
				onClick={close}
				className="absolute inset-0 bg-clarus-dark/40"
			/>
			<div
				role="alertdialog"
				aria-labelledby="audit-sent-title"
				aria-describedby="audit-sent-desc"
				className="relative w-full max-w-sm rounded-card border-[1.5px] border-clarus-dark bg-white p-6 md:p-8 text-center shadow-[0_8px_24px_-12px_rgba(0,0,0,0.35)]"
			>
				<button
					type="button"
					onClick={close}
					aria-label="Schließen"
					className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-clarus-dark/60 hover:bg-clarus-dark/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple"
				>
					<Icon name="close" className="text-[20px]" />
				</button>

				<span
					aria-hidden="true"
					className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-clarus-green/40 border-[1.5px] border-clarus-dark"
				>
					<Icon
						name="mark_email_read"
						className="text-[28px] text-clarus-dark"
					/>
				</span>

				<h2
					id="audit-sent-title"
					className="text-display-md mt-4 text-clarus-dark"
				>
					E-Mail wurde versendet
				</h2>
				<p id="audit-sent-desc" className="text-body mt-2 text-clarus-dark/75">
					Bitte bestätigen Sie Ihre E-Mail über den Link, den wir Ihnen
					geschickt haben — danach sehen Sie den vollständigen Bericht.
				</p>

				<Button
					type="button"
					variant="primary"
					onClick={close}
					className="mt-6 h-12 w-full"
				>
					Verstanden
				</Button>
			</div>
		</div>
	);
}

export function AuditSentPopup() {
	// useSearchParams must live inside a Suspense boundary.
	return (
		<React.Suspense fallback={null}>
			<Popup />
		</React.Suspense>
	);
}
